import { useEffect, useRef } from 'react';

import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';

import {
    readOnly$,
    iconComponentFor$,
    codeBlockLanguages$,
    codeMirrorExtensions$,
    useCodeBlockEditorContext,
    codeMirrorAutoLoadLanguageSupport$,
} from '@mdxeditor/editor';

import { useCellValues } from '@mdxeditor/gurx';
import { useCodeMirrorRef } from './useCodeMirrorRef';

import { languages } from '@codemirror/language-data';
import { basicSetup } from 'codemirror';

export const useInitCodePlugin = ({ nodeKey, language, code, focusEmitter }) => {
    const [readOnly, codeMirrorExtensions, autoLoadLanguageSupport] = useCellValues(
        readOnly$,
        codeMirrorExtensions$,
        codeMirrorAutoLoadLanguageSupport$,
        iconComponentFor$,
        codeBlockLanguages$
    );

    const codeMirrorRef = useCodeMirrorRef(nodeKey, 'codeblock', language, focusEmitter);
    const { setCode } = useCodeBlockEditorContext();
    const editorViewRef = useRef(null);
    const elRef = useRef(null);

    const setCodeRef = useRef(setCode);
    setCodeRef.current = setCode;
    codeMirrorRef.current = {
        getCodemirror: () => editorViewRef.current,
    };

    useEffect(() => {
        void (async () => {
            const extensions = [
                ...codeMirrorExtensions,
                basicSetup,
                EditorView.lineWrapping,
                EditorView.updateListener.of(({ state }) => {
                    setCodeRef.current(state.doc.toString());
                }),
            ];
            if (readOnly) {
                extensions.push(EditorState.readOnly.of(true));
            }
            if (language !== '' && autoLoadLanguageSupport) {
                const languageData = languages.find((l) => {
                    return (
                        l.name === language ||
                        l.alias.includes(language) ||
                        l.extensions.includes(language)
                    );
                });
                if (languageData) {
                    try {
                        const languageSupport = await languageData.load();
                        extensions.push(languageSupport.extension);
                    } catch (e) {
                        console.warn('failed to load language support for', language);
                    }
                }
            }
            elRef.current.innerHTML = '';
            editorViewRef.current = new EditorView({
                parent: elRef.current,
                state: EditorState.create({ doc: code, extensions }),
            });
        })();
        return () => {
            editorViewRef.current?.destroy();
            editorViewRef.current = null;
        };
    }, [readOnly, language]);

    return { elRef, readOnly };
};
